<template>
  <nav class="blog-pagination-nav mb-128">
    <NuxtLink
      v-if="page !== 1"
      class="blog-pagination-nav__controls blog-pagination-nav__controls--prev"
      :to="getPath(page - 1)"
    >
      <BaseIconSvg
        width="16"
        height="16"
        name="16-arrow-position-right"
        style="transform: rotate(180deg)"
      />
      <span class="blog-pagination-nav__control-label">Prev</span></NuxtLink
    >
    <div
      v-if="!isCurrentPageCloseToStart"
      class="blog-pagination-nav__quick-jump"
    >
      <NuxtLink class="blog-pagination-nav__page-link" :to="`/${path}`"
        >1</NuxtLink
      >
      <div class="blog-pagination-nav__decorator-dots">...</div>
    </div>

    <div class="blog-pagination-nav__page-numbers">
      <NuxtLink
        v-for="pageNumber in pageNumbersToDisplay"
        :key="`pageNumber-${pageNumber}`"
        :to="getPath(pageNumber)"
        :class="[
          'blog-pagination-nav__page-link',
          {
            'blog-pagination-nav__page-link--active': pageNumber === page,
          },
        ]"
        >{{ pageNumber }}</NuxtLink
      >
    </div>
    <div
      v-if="!isCurrentPageCloseToEnd"
      class="blog-pagination-nav__quick-jump"
    >
      <div class="blog-pagination-nav__decorator-dots">...</div>
      <NuxtLink
        class="blog-pagination-nav__page-link"
        :to="`/${path}/page-${totalPages}`"
        >{{ totalPages }}</NuxtLink
      >
    </div>

    <NuxtLink
      v-if="page !== totalPages"
      class="blog-pagination-nav__controls blog-pagination-nav__controls--next"
      :to="getPath(page + 1)"
      ><span class="blog-pagination-nav__control-label">Next</span>
      <BaseIconSvg width="16" height="16" name="16-arrow-position-right"
    /></NuxtLink>
  </nav>
</template>

<script lang="ts" setup>
interface Props {
  totalPages: number
  totalArticles: number
  page: number
  path: string
}

const props = defineProps<Props>()

const getPath = (nextPageNumber: number) => {
  if (nextPageNumber <= 1) {
    return `/${props.path}`
  }

  return `/${props.path}/page-${nextPageNumber}`
}

const isCurrentPageCloseToStart = computed(() => {
  return props.page <= 3
})

const isCurrentPageCloseToEnd = computed(() => {
  return props.page >= props.totalPages - 2
})

const pageNumbersToDisplay = computed(() => {
  const pageNumbers = []

  if (props.totalPages < 5) {
    for (let i = 1; i <= props.totalPages; i++) {
      pageNumbers.push(i)
    }

    return pageNumbers
  }

  if (isCurrentPageCloseToStart.value) {
    for (let i = 1; i <= 5; i++) {
      pageNumbers.push(i)
    }

    return pageNumbers
  }

  if (isCurrentPageCloseToEnd.value) {
    for (let i = props.totalPages - 4; i <= props.totalPages; i++) {
      pageNumbers.push(i)
    }

    return pageNumbers
  }

  for (let i = props.page - 2; i <= props.page + 2; i++) {
    pageNumbers.push(i)
  }

  return pageNumbers
})
</script>

<style lang="scss" scoped>
.blog-pagination-nav {
  display: flex;
  width: 100%;

  justify-content: center;
  align-items: center;
  font-size: var(--font-size-paragraph-s);
  font-weight: var(--font-weight-bold);
  height: 44px;

  a {
    &:hover {
      text-decoration: none;
      color: var(--color-link);
      svg {
        fill: var(--color-link);
      }
    }
  }

  &__quick-jump {
    display: flex;
    align-items: center;
  }

  &__page-link {
    padding-inline: 10px;
    color: var(--color-text);
    height: 100%;
    display: flex-inline;

    &--active {
      color: var(--color-link);
    }
  }

  &__decorator-dots {
    user-select: none;
    color: var(--navy-200);
  }

  &__control-label {
    display: none;

    @media (min-width: $breakpoint-md) {
      display: inline;
    }
  }

  &__controls {
    display: flex;
    align-items: center;
    color: var(--cobalt-800);
    padding-inline: 8px;
    height: 100%;
    justify-content: center;
    justify-self: center;
    align-self: center;

    &--prev {
      margin-right: 20px;
      span {
        margin-left: 8px;
      }
    }
    &--next {
      margin-left: 20px;
      span {
        margin-right: 8px;
      }
    }
  }
}
</style>
